import { getFirestore, collection, doc, getDoc, getDocs, query, where, orderBy, limit, startAfter, documentId, runTransaction, Timestamp, } from "firebase/firestore";
import { PageCursor } from "../../domain/models/pagination";
import { mapFirebaseError } from "./errorMapper";
import { ratingDTOtoDomain, ratingDomainToDTO } from "./mappers";
import { compositeRatingId } from "../../domain/models/rating";
export class FirebaseRatingsRepository {
    db = getFirestore();
    coll = collection(this.db, "ratings");
    async fetchRatingsPage(ratedUserId, pageSize, after) {
        try {
            let q = query(this.coll, where("ratedUserId", "==", ratedUserId), orderBy("createdAt", "desc"), limit(pageSize));
            if (after?.backing) {
                q = query(q, startAfter(after.backing));
            }
            const snap = await getDocs(q);
            const items = snap.docs.map((d) => ratingDTOtoDomain(d.id, d.data()));
            const nextCursor = snap.docs.length === pageSize ? new PageCursor(snap.docs[snap.docs.length - 1]) : null;
            return { items, nextCursor };
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async myRating(ratedUserId, raterUserId) {
        try {
            const id = compositeRatingId(ratedUserId, raterUserId);
            const snap = await getDoc(doc(this.coll, id));
            if (!snap.exists())
                return null;
            return ratingDTOtoDomain(snap.id, snap.data());
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async submit(input) {
        const id = compositeRatingId(input.ratedUserId, input.raterUserId);
        try {
            const result = await runTransaction(this.db, async (tx) => {
                const existingSnap = await tx.get(doc(this.coll, id));
                const isEdit = existingSnap.exists();
                const oldScore = isEdit ? existingSnap.data().score : 0;
                const userSnap = await tx.get(doc(this.db, "users", input.ratedUserId));
                if (!userSnap.exists())
                    throw new Error("rated user not found");
                const userData = userSnap.data();
                const ratingSum = userData.ratingSum ?? 0;
                const ratingCount = userData.ratingCount ?? 0;
                let newSum;
                let newCount;
                if (isEdit) {
                    newSum = ratingSum - oldScore + input.score;
                    newCount = ratingCount;
                }
                else {
                    newSum = ratingSum + input.score;
                    newCount = ratingCount + 1;
                }
                const newAverage = newCount > 0 ? Math.round((newSum / newCount) * 10) / 10 : 0;
                tx.set(doc(this.coll, id), { ...ratingDomainToDTO(input), updatedAt: Timestamp.now() });
                tx.update(doc(this.db, "users", input.ratedUserId), {
                    ratingSum: newSum,
                    ratingCount: newCount,
                    ratingAverage: newAverage,
                });
                return {
                    ...input,
                    id,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                };
            });
            return result;
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async fetchById(id) {
        try {
            const snap = await getDoc(doc(this.coll, id));
            if (!snap.exists())
                return null;
            return ratingDTOtoDomain(snap.id, snap.data());
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
    async aggregates(userIds) {
        const result = new Map();
        if (userIds.length === 0)
            return result;
        try {
            for (let i = 0; i < userIds.length; i += 30) {
                const chunk = userIds.slice(i, i + 30);
                const q = query(this.coll, where(documentId(), "in", chunk.map((uid) => `${uid}_`)));
                const snap = await getDocs(q);
                const byUser = new Map();
                snap.docs.forEach((d) => {
                    const data = d.data();
                    const uid = data.ratedUserId;
                    const entry = byUser.get(uid) ?? { sum: 0, count: 0 };
                    entry.sum += data.score;
                    entry.count += 1;
                    byUser.set(uid, entry);
                });
                chunk.forEach((uid) => {
                    const entry = byUser.get(uid);
                    result.set(uid, {
                        averageScore: entry ? Math.round((entry.sum / entry.count) * 10) / 10 : 0,
                        count: entry?.count ?? 0,
                    });
                });
            }
            return result;
        }
        catch (e) {
            throw mapFirebaseError(e);
        }
    }
}
//# sourceMappingURL=FirebaseRatingsRepository.js.map